/**
 * Local Bottle clicker prefs — a click bind, not the save.
 * Stored separately so a cloud wipe does not steal someone's Space bar.
 */

export type BottleClickerClickBind =
  | { kind: "key"; code: string }
  | { kind: "mouse"; button: number };

export interface BottleClickerSettings {
  clickBind: BottleClickerClickBind | null;
  /** When false, Bottle clicker SFX stay silent. */
  sound: boolean;
  /** Master SFX level from 0 (silent) to 1 (full). */
  volume: number;
}

/** Clamp / coerce a persisted volume into 0..1. */
export function clampBottleClickerVolume(raw: unknown): number {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return 0.65;
  return Math.min(1, Math.max(0, raw));
}

const BLOCKED_KEY_CODES = new Set([
  "Escape",
  "Tab",
  "MetaLeft",
  "MetaRight",
  "AltLeft",
  "AltRight",
  "ControlLeft",
  "ControlRight",
  "ContextMenu",
  "LaunchWebApp",
  "BrowserBack",
  "BrowserForward",
]);

export function emptyBottleClickerSettings(): BottleClickerSettings {
  return { clickBind: null, sound: true, volume: 0.65 };
}

export function isBindableKeyCode(code: string): boolean {
  if (!code || code === "Unidentified") return false;
  if (BLOCKED_KEY_CODES.has(code)) return false;
  if (/^F\d{1,2}$/.test(code)) return false;
  return true;
}

/** Left click already taps the field; right click keeps the context menu. */
export function isBindableMouseButton(button: number): boolean {
  return button === 1 || button === 3 || button === 4;
}

export function clickBindFromKeyCode(code: string): BottleClickerClickBind | null {
  return isBindableKeyCode(code) ? { kind: "key", code } : null;
}

export function clickBindFromMouseButton(button: number): BottleClickerClickBind | null {
  return isBindableMouseButton(button) ? { kind: "mouse", button } : null;
}

export function clickBindsEqual(
  a: BottleClickerClickBind | null,
  b: BottleClickerClickBind | null,
): boolean {
  if (a === b) return true;
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === "key" && b.kind === "key") return a.code === b.code;
  return a.kind === "mouse" && b.kind === "mouse" && a.button === b.button;
}

export function formatClickBind(bind: BottleClickerClickBind | null): string {
  if (!bind) return "Brak";
  if (bind.kind === "mouse") {
    if (bind.button === 1) return "Środkowy przycisk";
    if (bind.button === 3) return "Boczny przycisk (wstecz)";
    if (bind.button === 4) return "Boczny przycisk (naprzód)";
    return `Przycisk myszy ${bind.button + 1}`;
  }
  return formatKeyCode(bind.code);
}

export function parseBottleClickerSettings(raw: unknown): BottleClickerSettings {
  if (!raw || typeof raw !== "object") return emptyBottleClickerSettings();
  const record = raw as Record<string, unknown>;
  return {
    clickBind: parseClickBind(record.clickBind),
    sound: typeof record.sound === "boolean" ? record.sound : true,
    volume: clampBottleClickerVolume(record.volume),
  };
}

function parseClickBind(raw: unknown): BottleClickerClickBind | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  if (record.kind === "key" && typeof record.code === "string") {
    return clickBindFromKeyCode(record.code);
  }
  if (record.kind === "mouse" && typeof record.button === "number") {
    return clickBindFromMouseButton(record.button);
  }
  return null;
}

function formatKeyCode(code: string): string {
  if (code === "Space") return "Spacja";
  if (code === "Enter" || code === "NumpadEnter") return "Enter";
  if (code === "Backspace") return "Backspace";
  if (code === "ShiftLeft") return "Lewy Shift";
  if (code === "ShiftRight") return "Prawy Shift";
  if (code.startsWith("Key") && code.length === 4) return code.slice(3);
  if (code.startsWith("Digit") && code.length === 6) return code.slice(5);
  if (code.startsWith("Numpad") && code.length > 6) return `Num ${code.slice(6)}`;
  const named: Record<string, string> = {
    Minus: "-",
    Equal: "=",
    BracketLeft: "[",
    BracketRight: "]",
    Backslash: "\\",
    Semicolon: ";",
    Quote: "'",
    Backquote: "`",
    Comma: ",",
    Period: ".",
    Slash: "/",
    ArrowUp: "↑",
    ArrowDown: "↓",
    ArrowLeft: "←",
    ArrowRight: "→",
  };
  return named[code] ?? code;
}
